import { List, Box, Typography } from "@mui/joy";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";
import { Session } from "@supabase/supabase-js";
import { getCompletedProblems } from "../utils/getCompletedProblems";
import { ContractProgress, Problem, Progress } from "../types";
import CategoryLock from "../utils/CategoryLock";
import CategoryListItems from "./CategoryListItem";
import BlogMenuButton from "./BlogMenuButton";
import { getCategoryListOrdered } from "../utils/getCategoryListOrdered";
import { fetchCategories } from "../utils/TestCategoriesFetch";

export interface CategoryListProps {
  searchedProblems: Problem[];
  activeCategory: string | null;
  onSelectCategory: (cat: string) => void;
  session: Session | null;
  contractProgress: ContractProgress;
  kbFocus: string;
  kbSelectedCategory: string | null;
}

export default function CategoryList({
  searchedProblems,
  activeCategory,
  onSelectCategory,
  session,
  contractProgress,
  kbFocus,
  kbSelectedCategory
}: CategoryListProps) {
  const [error, setError] = useState("");
  const [progress, setProgress] = useState<Progress[]>([]);
  const [fetchedCategories, setFetchedCategories] = useState<Map<string, boolean>>();

  useEffect(() => {
    (async () => {
      const categoryResult = await fetchCategories();

      if (!categoryResult) return;
      setFetchedCategories(categoryResult)
    })();
  }, []);

  // List of categories that show up in search results.
  const categories = getCategoryListOrdered(searchedProblems
    .filter((c) => c.meta.question_type[0] !== "test")
    .map((c) => c.meta.category)
    .filter((c, index, array) => array.indexOf(c) === index)
    .sort((a, b) => a.localeCompare(b)));

  const specialCategories = [];
  if (searchedProblems.some((c) => c.meta.question_type[0] === "haystack")) {
    specialCategories.push("haystack");
  }
  if (searchedProblems.some((c) => c.meta.question_type[0] === "mutation")) {
    specialCategories.push("mutation");
  }

  const testCategories = searchedProblems
    .filter((c) => c.meta.question_type[0] === "test")
    .map((c) => c.meta.category)
    .filter((c, index, array) => array.indexOf(c) === index);

  useEffect(() => {
    async function fetchProgress() {
      if (!session) return;
      const { user } = session;

      const { data: submissions, error } = await supabase
        .from("submissions")
        .select("problem_title, passed_tests, total_tests, question_type")
        .eq("profile_id", user.id);

      if (error) {
        setError(error.message);
      }

      const summary = getCompletedProblems(submissions || []);

      setProgress(summary);
    }

    fetchProgress();
  }, [session]);

  const categoryLock = useMemo(() => new CategoryLock(progress), [progress]);

  /**
   * Maps the string of the category name to the corresponding Lock object.
   *
   * **TODO: This function should probably be moved inside the CategoryLock class.**
   */
  function mapCategoryToLock(category: string) {
    switch (category) {
      case "Level 0":
        return categoryLock.level_0;
      case "Fundamentals":
        return categoryLock.fundamentals;
      case "Logic":
        return categoryLock.logic;
      case "String-1":
        return categoryLock.string_1;
      case "String-2":
        return categoryLock.string_2;
      case "List-1: Indexing":
        return categoryLock.list_1;
      case "List-2: Iterating":
        return categoryLock.list_2;
      case "Level-3: Complex Problems":
        return categoryLock.level_3;
      case "Mutation":
        return categoryLock.mutation;
      case "Haystack":
        return categoryLock.haystack;
      default:
        return categoryLock.level_0;
    }
  }

  if (error) {
    return <Typography>Error fetching categories: {error}</Typography>;
  }

  return (
    <List component="nav" sx={{ py: 2 }}>
      <>
        <BlogMenuButton
          category="blogs"
          activeCategory={activeCategory}
          onSelectCategory={onSelectCategory}
          kbFocus={kbFocus}
          kbSelectedCategory={kbSelectedCategory}
        />
        <Box>
          <hr />
        </Box>
        <CategoryListItems
          categories={categories}
          type="category"
          progress={progress}
          mapCategoryToLock={mapCategoryToLock}
          activeCategory={activeCategory}
          onSelectCategory={onSelectCategory}
          session={session}
          contractProgress={contractProgress}
          kbFocus={kbFocus}
          kbSelectedCategory={kbSelectedCategory}
          fetchedCategories={fetchedCategories}
        />
        <Box>
          <hr />
        </Box>
        <CategoryListItems
          categories={specialCategories}
          type="question_type"
          progress={progress}
          mapCategoryToLock={mapCategoryToLock}
          activeCategory={activeCategory}
          onSelectCategory={onSelectCategory}
          session={session}
          contractProgress={contractProgress}
          kbFocus={kbFocus}
          kbSelectedCategory={kbSelectedCategory}
          fetchedCategories={fetchedCategories}
        />
        <Box>
          <hr />
        </Box>
        <CategoryListItems
          categories={testCategories}
          type="category"
          progress={progress}
          mapCategoryToLock={mapCategoryToLock}
          activeCategory={activeCategory}
          onSelectCategory={onSelectCategory}
          session={session}
          contractProgress={contractProgress}
          kbFocus={kbFocus}
          kbSelectedCategory={kbSelectedCategory}
          fetchedCategories={fetchedCategories}
        />
      </>
    </List>
  );
}
